import moment from 'moment-timezone';
import { isUuid } from '../../activity_tracker/route_patterns';

// The page answers "what did this CMS user do on Hridaya", read by people in India, so every time
// is shown and every picked day is meant in IST, whatever zone the header dropdown is set to.
export const IST = 'Asia/Kolkata';

// goserver's reader answers 400 for a window over 31 days (plan Contract, S6), so the page
// refuses it before the request instead of showing a server error.
export const MAX_RANGE_DAYS = 31;

// The reader's default page_size; its max is 200, and 50 rows is a screen and a half.
export const PAGE_SIZE = 50;

export const EVENT_NAMES = ['page_view', 'interaction', 'form_submit'];

// What people read for each event name, on CMS Activity and on the Student tab's CMS Views.
// Display only: the wire value (filters, ingest) stays the raw name. interaction is "Interaction",
// not "Click": the tracker records field changes under it too.
const EVENT_LABELS = {
  page_view: 'Page view',
  interaction: 'Interaction',
  form_submit: 'Form submit'
};
// "ADD_CREDITS" or "page_view" as words. Lives here so the Student tab and the CMS Activity change
// rows read an action the same way.
// eslint-disable-next-line camelcase -- the name StudentActivity.jsx already uses for it
export const humanize_event_name = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');

export const eventLabel = (name) => EVENT_LABELS[name] || name;

export const defaultDates = () => {
  const today = moment().tz(IST);
  return {
    fromDate: today.clone().subtract(6, 'days').format('YYYY-MM-DD'),
    toDate: today.format('YYYY-MM-DD')
  };
};

// Picked days are inclusive IST days; the reader takes epoch ms in a half open [from, to), so the
// end is the start of the IST day after the last one picked.
export const istRange = (fromDate, toDate) => {
  const from = moment.tz(fromDate, 'YYYY-MM-DD', true, IST);
  const to = moment.tz(toDate, 'YYYY-MM-DD', true, IST).add(1, 'day');
  if (!from.isValid() || !to.isValid() || !from.isBefore(to)) {
    return { error: 'Pick a start date on or before the end date.' };
  }
  if (to.diff(from, 'days') > MAX_RANGE_DAYS) {
    return { error: `Pick a range of ${MAX_RANGE_DAYS} days or fewer.` };
  }
  return { from: from.valueOf(), to: to.valueOf() };
};

// Native min/max on the two date inputs, so the picker itself cannot reach a window longer than
// MAX_RANGE_DAYS (inclusive days, hence the minus one) or an end before the start.
export const dateLimits = (fromDate, toDate) => {
  const span = MAX_RANGE_DAYS - 1;
  const day = (date, shift) =>
    date ? moment(date, 'YYYY-MM-DD', true).add(shift, 'days').format('YYYY-MM-DD') : undefined;
  return {
    from: { min: day(toDate, -span), max: toDate || undefined },
    to: { min: fromDate || undefined, max: day(fromDate, span) }
  };
};

// The query for one page, or the reason it cannot be sent. A malformed uuid would come back as a
// 400 for the whole read, so it is caught here with a message naming the field.
export const buildQuery = (filters, page) => {
  const range = istRange(filters.fromDate, filters.toDate);
  if (range.error) return { error: range.error };
  const query = new URLSearchParams({
    from: String(range.from),
    to: String(range.to),
    page: String(page),
    page_size: String(PAGE_SIZE)
  });
  const uuids = {
    cms_user_uuid: filters.cmsUserUuid.trim(),
    entity_uuid: filters.entityUuid.trim()
  };
  const bad = Object.keys(uuids).find((key) => uuids[key] && !isUuid(uuids[key]));
  if (bad) return { error: `${bad === 'cms_user_uuid' ? 'CMS user' : 'Entity'} must be a uuid.` };
  Object.keys(uuids).forEach((key) => uuids[key] && query.set(key, uuids[key]));
  query.set('kind', filters.kind);
  // A change row has no module and its event_name is the action, so goserver drops the change half
  // when either is set. Sending a value left over from Views mode would silently empty Changes.
  if (filters.kind !== 'change') {
    if (filters.module.trim()) query.set('module', filters.module.trim());
    if (filters.eventName) query.set('event_name', filters.eventName);
  }
  return { query: query.toString() };
};

// Each row shows only its clock time ("1:59 PM"): the day is in the sticky day header above it, so
// repeating "26 Oct 2026," on every row was 13 characters of noise. No zone suffix: the result bar
// says "Times in IST".
export const formatClock = (ms) => moment.tz(ms, IST).format('h:mm A');

// The row's hover title, to the second, for an audit that needs to line a row up with a log.
export const formatFull = (ms) => moment.tz(ms, IST).format('ddd D MMM YYYY, h:mm:ss A [IST]');

// "20 Oct to 26 Oct 2026" for the result bar and the empty state; the start keeps its year only
// when the range crosses one.
export const rangeLabel = (fromDate, toDate) => {
  const from = moment(fromDate, 'YYYY-MM-DD', true);
  const to = moment(toDate, 'YYYY-MM-DD', true);
  return `${from.format(from.year() === to.year() ? 'D MMM' : 'D MMM YYYY')} to ${to.format(
    'D MMM YYYY'
  )}`;
};

// The reader returns rows newest first; groups keep that order. Days are IST calendar days: a row
// at 23:59 IST and one at 00:01 IST are different days here but the same UTC day, so grouping by
// the browser's or UTC's date would merge them. The year shows only when it is not this IST year.
const RELATIVE_DAYS = { 0: 'Today · ', 1: 'Yesterday · ' };
export const groupByIstDay = (rows) => {
  const today = moment().tz(IST).startOf('day');
  const groups = [];
  rows.forEach((row) => {
    const day = moment.tz(row.occurred_at, IST);
    const key = day.format('YYYY-MM-DD');
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      const ago = today.diff(day.clone().startOf('day'), 'days');
      const date = day.format(day.year() === today.year() ? 'ddd D MMM' : 'ddd D MMM YYYY');
      group = { key, label: `${RELATIVE_DAYS[ago] || ''}${date}`, rows: [] };
      groups.push(group);
    }
    group.rows.push(row);
  });
  return groups;
};

// Avatar letters: the first letter of the first two words, or the first two letters of a one word
// name ("SH" for Shweta). With no name (the user list failed) the uuid's first two characters, so
// the same person still gets the same mark on every row.
export const initials = (name, uuid) => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return uuid.slice(0, 2).toUpperCase();
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

// One line a reader can scan. Only fields the reader returns are used; none of them carries what
// was typed or shown, because the tracker never records it.
// A control the tracker could not name arrives as `unnamed:<kind>` (tracker.js describeControl), so
// it reads by its kind instead: "Clicked a button", "Changed a select". The tracker records field
// edits (input, select, textarea) on change and everything else on click, which picks the verb.
const FIELD_KINDS = ['input', 'select', 'textarea'];
// control_kind is the role or the lower-case tag name, so an anchor arrives as `a`.
const KIND_WORDS = { a: 'link', textarea: 'text box' };
const withArticle = (kind) => {
  const word = KIND_WORDS[kind] || kind;
  return `${/^[aeiou]/i.test(word) ? 'an' : 'a'} ${word}`;
};

export const describeRow = (row) => {
  if (row.kind === 'change') return row.change.summary || humanize_event_name(row.event_name);
  const unnamed = !row.control || row.control.startsWith('unnamed:');
  const kind = row.control_kind || 'control';
  if (row.event_name === 'page_view') return `Viewed ${row.route}`;
  if (row.event_name === 'form_submit') {
    return unnamed ? 'Submitted a form' : `Submitted ${row.control}`;
  }
  const verb = FIELD_KINDS.includes(kind) ? 'Changed' : 'Clicked';
  if (unnamed) return `${verb} ${withArticle(kind)}`;
  return `Used ${row.control}${row.control_kind ? ` (${row.control_kind})` : ''}`;
};

// The CMS Activity row's What cell: a bold label and the route beside it, the route in the same
// place on every row. describeRow stays the one-line form for the Student tab's CMS Views, which has
// no route column; this is not a second wording, only the page_view line split in two.
export const describeParts = (row) => ({
  label: row.event_name === 'page_view' ? 'Viewed page' : describeRow(row),
  route: row.route
});
